import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ProviderCard } from '@/components/integrations';
import * as api from '@/lib/integrations/api';
import type { ProviderResponse, UserConnection } from '@/lib/integrations/types';
import { cn } from '@/lib/utils';
import { Search, ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/_dashboard/integrations/add')({
  component: AddIntegrationPage,
});

function AddIntegrationPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Data state
  const [providers, setProviders] = useState<ProviderResponse[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch providers, categories, and user connections on mount
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [providersData, categoriesData, connectionsData] = await Promise.all([
          api.getProviders(),
          api.getCategories(),
          api.getUserConnections(),
        ]);
        setProviders(providersData);
        setCategories(categoriesData);
        setConnections(connectionsData);
      } catch (err) {
        setError((err as Error).message || 'Failed to load providers');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Map of configured providers with connection counts
  const configuredProviders = useMemo(() => {
    const map = new Map<string, number>();
    connections.forEach((conn) => {
      const count = map.get(conn.provider_config_key) || 0;
      map.set(conn.provider_config_key, count + 1);
    });
    return map;
  }, [connections]);

  // Sort categories with "popular" first
  const sortedCategories = useMemo(() => {
    const popular = categories.filter((c) => c.toLowerCase() === 'popular');
    const others = categories.filter((c) => c.toLowerCase() !== 'popular').sort();
    return [...popular, ...others];
  }, [categories]);

  // Calculate category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: providers.length,
      configured: configuredProviders.size,
    };
    providers.forEach((p) => {
      p.categories?.forEach((cat) => {
        const key = cat.toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }, [providers, configuredProviders]);

  // Filter providers by search and category
  const filteredProviders = providers.filter((p) => {
    const matchesSearch =
      !search ||
      p.provider.toLowerCase().includes(search.toLowerCase()) ||
      (p.display_name?.toLowerCase() || '').includes(search.toLowerCase());

    let matchesCategory = false;
    if (selectedCategory === 'all') {
      matchesCategory = true;
    } else if (selectedCategory === 'configured') {
      matchesCategory = configuredProviders.has(p.provider);
    } else {
      matchesCategory = p.categories?.some(
        (c) => c.toLowerCase() === selectedCategory.toLowerCase()
      ) ?? false;
    }

    return matchesSearch && matchesCategory;
  });

  const handleProviderClick = (provider: ProviderResponse) => {
    navigate({ to: `/integrations/${provider.provider}` });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/integrations' })}
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Add Integration</h2>
          <p className="text-muted-foreground">
            Choose a provider to connect with your account
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search providers..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2">
        <Badge
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          className="cursor-pointer transition-colors"
          onClick={() => setSelectedCategory('all')}
        >
          All ({categoryCounts.all || 0})
        </Badge>
        {configuredProviders.size > 0 && (
          <Badge
            variant={selectedCategory === 'configured' ? 'default' : 'outline'}
            className="cursor-pointer transition-colors"
            onClick={() => setSelectedCategory('configured')}
          >
            Configured ({categoryCounts.configured || 0})
          </Badge>
        )}
        {sortedCategories.map((cat) => (
          <Badge
            key={cat}
            variant={selectedCategory === cat ? 'default' : 'outline'}
            className={cn('cursor-pointer transition-colors capitalize')}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat} ({categoryCounts[cat.toLowerCase()] || 0})
          </Badge>
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Skeleton className="size-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredProviders.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {search || selectedCategory !== 'all'
              ? 'No providers match your filters'
              : 'No providers available'}
          </p>
        </div>
      )}

      {/* Provider grid - responsive */}
      {!loading && !error && filteredProviders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredProviders.map((provider) => (
            <ProviderCard
              key={provider.unique_key || provider.provider}
              provider={provider}
              connectionCount={configuredProviders.get(provider.provider)}
              onClick={() => handleProviderClick(provider)}
            />
          ))}
        </div>
      )}

      {/* Provider count */}
      {!loading && !error && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {filteredProviders.length} of {providers.length} providers
        </p>
      )}
    </div>
  );
}
